import { canonicalPublicUrl } from './public-url.js';

export const COMPLETED_PATH_CARD_WIDTH = 1200;
export const COMPLETED_PATH_CARD_HEIGHT = 630;

const MAX_CARD_NAME_LENGTH = 100;
const MAX_CHAIN_NAMES = 5;

const COLORS = {
  background: '#04070f',
  panel: '#071321',
  panelBorder: '#17354b',
  cyan: '#86dfff',
  cyanMuted: '#7e9bb5',
  gold: '#ffd54a',
  white: '#dcf3ff',
} as const;

export type CompletedPathCardInput = Readonly<{
  fromName: string;
  toName: string;
  stepCount: number;
  /** Full ordered path, including both endpoint names. */
  chainNames: readonly string[];
  snapshotDate: string;
  canonicalUrl: string;
}>;

export type CompletedPathCardArtifact = Readonly<{
  canvas: HTMLCanvasElement;
  blob: Blob;
  fileName: string;
  title: string;
  text: string;
}>;

export type CompletedPathCardDelivery = 'shared' | 'downloaded' | 'cancelled' | 'unavailable';

type NormalizedCardInput = Readonly<{
  fromName: string;
  toName: string;
  stepCount: number;
  chainNames: readonly string[];
  snapshotDate: string;
  canonicalUrl: string;
}>;

function cleanCardText(value: string, label: string, maximumLength = MAX_CARD_NAME_LENGTH): string {
  const cleaned = value.normalize('NFC').replace(/\s+/gu, ' ').trim();
  if (cleaned.length === 0) {
    throw new Error(`${label} is required.`);
  }
  return cleaned.slice(0, maximumLength);
}

function normalizedCardInput(input: CompletedPathCardInput): NormalizedCardInput {
  if (!Number.isSafeInteger(input.stepCount) || input.stepCount < 1) {
    throw new Error('A completed path card requires a positive whole-number step count.');
  }
  if (input.chainNames.length !== input.stepCount + 1) {
    throw new Error(
      'The full path chain must contain exactly one more person than its step count.',
    );
  }

  const fromName = cleanCardText(input.fromName, 'The first endpoint name');
  const toName = cleanCardText(input.toName, 'The second endpoint name');
  const chainNames = input.chainNames.map((name) => cleanCardText(name, 'Every path name'));
  if (chainNames[0] !== fromName || chainNames.at(-1) !== toName) {
    throw new Error('The path chain must begin and end with the supplied endpoint names.');
  }
  if (!/^\d{4}-\d{2}-\d{2}$/u.test(input.snapshotDate)) {
    throw new Error('The data snapshot must use YYYY-MM-DD format.');
  }

  return {
    fromName,
    toName,
    stepCount: input.stepCount,
    chainNames,
    snapshotDate: input.snapshotDate,
    canonicalUrl: canonicalPublicUrl(input.canonicalUrl),
  };
}

export function abbreviateCompletedPathChain(
  names: readonly string[],
  maximumNames = MAX_CHAIN_NAMES,
): readonly string[] {
  if (!Number.isSafeInteger(maximumNames) || maximumNames < 3) {
    throw new Error('An abbreviated path must reserve at least three positions.');
  }
  if (names.length <= maximumNames) {
    return [...names];
  }
  const visibleNames = maximumNames - 1;
  const leadingNames = Math.ceil(visibleNames / 2);
  const trailingNames = Math.floor(visibleNames / 2);
  return [...names.slice(0, leadingNames), '...', ...names.slice(-trailingNames)];
}

function shortenChainName(value: string): string {
  return value.length <= 30 ? value : `${value.slice(0, 27).trimEnd()}...`;
}

function setFittedFont(
  context: CanvasRenderingContext2D,
  text: string,
  maximumWidth: number,
  maximumSize: number,
  minimumSize: number,
  weight: number,
  family: string,
): number {
  let size = maximumSize;
  for (; size > minimumSize; size -= 1) {
    context.font = `${weight} ${size}px ${family}`;
    if (context.measureText(text).width <= maximumWidth) {
      return size;
    }
  }
  context.font = `${weight} ${minimumSize}px ${family}`;
  return minimumSize;
}

function drawBrandMark(context: CanvasRenderingContext2D): void {
  context.strokeStyle = COLORS.cyan;
  context.fillStyle = COLORS.cyan;
  context.lineWidth = 3;
  context.beginPath();
  context.moveTo(68, 75);
  context.lineTo(92, 48);
  context.lineTo(116, 75);
  context.stroke();
  for (const [x, y] of [
    [68, 75],
    [92, 48],
    [116, 75],
  ] as const) {
    context.beginPath();
    context.arc(x, y, 7, 0, Math.PI * 2);
    context.fill();
  }
}

function drawBackground(context: CanvasRenderingContext2D): void {
  const gradient = context.createLinearGradient(0, 0, COMPLETED_PATH_CARD_WIDTH, 0);
  gradient.addColorStop(0, COLORS.background);
  gradient.addColorStop(0.72, '#07111d');
  gradient.addColorStop(1, '#0a1420');
  context.fillStyle = gradient;
  context.fillRect(0, 0, COMPLETED_PATH_CARD_WIDTH, COMPLETED_PATH_CARD_HEIGHT);

  context.fillStyle = 'rgba(134, 223, 255, 0.22)';
  for (const [x, y, radius] of [
    [1040, 82, 3],
    [1110, 158, 2],
    [1002, 272, 2],
    [1080, 372, 4],
    [1140, 492, 2],
    [820, 96, 2],
    [924, 526, 3],
  ] as const) {
    context.beginPath();
    context.arc(x, y, radius, 0, Math.PI * 2);
    context.fill();
  }
}

function drawChain(context: CanvasRenderingContext2D, chainNames: readonly string[]): void {
  const names = abbreviateCompletedPathChain(chainNames).map(shortenChainName);
  const midpoint = Math.ceil(names.length / 2);
  const lines = [names.slice(0, midpoint), names.slice(midpoint)].filter((line) => line.length > 0);

  context.fillStyle = COLORS.panel;
  context.strokeStyle = COLORS.panelBorder;
  context.lineWidth = 2;
  context.beginPath();
  context.roundRect(58, 392, 1084, 112, 14);
  context.fill();
  context.stroke();

  context.fillStyle = COLORS.cyanMuted;
  context.font = '700 14px ui-monospace, SFMono-Regular, Menlo, monospace';
  context.fillText('RELATIONSHIP CHAIN', 82, 424);

  context.fillStyle = COLORS.white;
  for (const [lineIndex, line] of lines.entries()) {
    const label = line.join('  ->  ');
    setFittedFont(
      context,
      label,
      1032,
      lines.length === 1 ? 24 : 20,
      13,
      600,
      'system-ui, sans-serif',
    );
    context.fillText(label, 82, 462 + lineIndex * 27);
  }
}

/** Render the deterministic card into a supplied canvas or a new browser canvas. */
export function renderCompletedPathCard(
  input: CompletedPathCardInput,
  canvas?: HTMLCanvasElement,
): HTMLCanvasElement {
  const normalized = normalizedCardInput(input);
  if (canvas === undefined && typeof document === 'undefined') {
    throw new Error('Completed path cards require a browser canvas.');
  }
  const output = canvas ?? document.createElement('canvas');
  output.width = COMPLETED_PATH_CARD_WIDTH;
  output.height = COMPLETED_PATH_CARD_HEIGHT;
  const context = output.getContext('2d');
  if (context === null) {
    throw new Error('Completed path card canvas is unavailable.');
  }

  drawBackground(context);
  drawBrandMark(context);

  context.textAlign = 'left';
  context.textBaseline = 'alphabetic';
  context.fillStyle = COLORS.white;
  context.font = '700 24px ui-monospace, SFMono-Regular, Menlo, monospace';
  context.fillText('ENTANGLED VEGA', 150, 67);
  context.fillStyle = COLORS.cyanMuted;
  context.font = '500 13px ui-monospace, SFMono-Regular, Menlo, monospace';
  context.fillText('PUBLIC-FIGURE CONNECTION ATLAS', 151, 88);

  const stepLabel = `${normalized.stepCount} SOURCE-LINKED ${normalized.stepCount === 1 ? 'STEP' : 'STEPS'}`;
  context.fillStyle = COLORS.gold;
  context.font = '700 16px ui-monospace, SFMono-Regular, Menlo, monospace';
  context.fillText(stepLabel, 58, 146);

  context.fillStyle = COLORS.white;
  setFittedFont(context, normalized.fromName, 920, 48, 28, 700, 'system-ui, sans-serif');
  context.fillText(normalized.fromName, 58, 220);
  context.fillStyle = COLORS.cyan;
  context.font = '700 28px system-ui, sans-serif';
  context.fillText('to', 58, 268);
  context.fillStyle = COLORS.white;
  setFittedFont(context, normalized.toName, 920, 48, 28, 700, 'system-ui, sans-serif');
  context.fillText(normalized.toName, 58, 334);

  drawChain(context, normalized.chainNames);

  context.fillStyle = COLORS.gold;
  context.font = '700 14px ui-monospace, SFMono-Regular, Menlo, monospace';
  context.fillText(`DATA SNAPSHOT ${normalized.snapshotDate}`, 58, 574);
  context.textAlign = 'right';
  context.fillStyle = COLORS.cyanMuted;
  setFittedFont(
    context,
    normalized.canonicalUrl,
    670,
    14,
    10,
    600,
    'ui-monospace, SFMono-Regular, Menlo, monospace',
  );
  context.fillText(normalized.canonicalUrl, 1142, 574);
  context.textAlign = 'left';

  return output;
}

function completedPathCardFileName(input: NormalizedCardInput): string {
  const slug = `${input.fromName}-to-${input.toName}`
    .normalize('NFKD')
    .replace(/[^A-Za-z0-9]+/gu, '-')
    .replace(/^-+|-+$/gu, '')
    .toLowerCase()
    .slice(0, 72);
  return `entangled-vega-${slug.length > 0 ? slug : 'relationship-path'}-${input.snapshotDate}.png`;
}

function canvasPngBlob(canvas: HTMLCanvasElement): Promise<Blob> {
  return new Promise((resolve, reject) => {
    canvas.toBlob((blob) => {
      if (blob === null) {
        reject(new Error('Completed path card encoding failed.'));
        return;
      }
      resolve(blob);
    }, 'image/png');
  });
}

export async function createCompletedPathCard(
  input: CompletedPathCardInput,
  canvas?: HTMLCanvasElement,
): Promise<CompletedPathCardArtifact> {
  const normalized = normalizedCardInput(input);
  const output = renderCompletedPathCard(normalized, canvas);
  const blob = await canvasPngBlob(output);
  const stepText = `${normalized.stepCount} source-linked ${normalized.stepCount === 1 ? 'step' : 'steps'}`;
  return {
    canvas: output,
    blob,
    fileName: completedPathCardFileName(normalized),
    title: `${normalized.fromName} to ${normalized.toName} - Entangled Vega`,
    text: `${normalized.fromName} connects to ${normalized.toName} in ${stepText} on Entangled Vega.`,
  };
}

function pathCardFile(artifact: CompletedPathCardArtifact): File | null {
  if (typeof File === 'undefined') {
    return null;
  }
  return new File([artifact.blob], artifact.fileName, {
    type: 'image/png',
    lastModified: 0,
  });
}

export function canShareCompletedPathCardFile(artifact: CompletedPathCardArtifact): boolean {
  const file = pathCardFile(artifact);
  if (
    file === null ||
    typeof navigator === 'undefined' ||
    typeof navigator.share !== 'function' ||
    typeof navigator.canShare !== 'function'
  ) {
    return false;
  }
  try {
    return navigator.canShare({ files: [file] });
  } catch {
    return false;
  }
}

export function downloadCompletedPathCard(artifact: CompletedPathCardArtifact): boolean {
  if (
    typeof document === 'undefined' ||
    typeof URL === 'undefined' ||
    typeof URL.createObjectURL !== 'function'
  ) {
    return false;
  }
  const objectUrl = URL.createObjectURL(artifact.blob);
  const anchor = document.createElement('a');
  anchor.href = objectUrl;
  anchor.download = artifact.fileName;
  anchor.hidden = true;
  document.body.append(anchor);
  anchor.click();
  anchor.remove();
  globalThis.setTimeout(() => URL.revokeObjectURL(objectUrl), 1_000);
  return true;
}

/** Share the PNG when file sharing is supported; otherwise download it. */
export async function shareOrDownloadCompletedPathCard(
  artifact: CompletedPathCardArtifact,
): Promise<CompletedPathCardDelivery> {
  const file = pathCardFile(artifact);
  if (file !== null && canShareCompletedPathCardFile(artifact)) {
    try {
      await navigator.share({
        files: [file],
        title: artifact.title,
        text: artifact.text,
      });
      return 'shared';
    } catch (error) {
      if (error instanceof Error && error.name === 'AbortError') {
        return 'cancelled';
      }
    }
  }
  return downloadCompletedPathCard(artifact) ? 'downloaded' : 'unavailable';
}
